import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Container,
  Dialog,
  DialogContent,
  FormControl,
  IconButton,
  InputLabel,
  Link as MUILink,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Cancel,
  Close as CloseIcon,
  Download,
  Edit as EditIcon,
  Home,
  Inventory as InboxIcon,
  ShoppingCart,
} from '@mui/icons-material';
import { utils, write } from '@e965/xlsx';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { type Dayjs } from 'dayjs';
import { useNavigate } from 'react-router-dom';
import {
  useOrders,
  useCatalogNonBuy,
  useCompletePurchaseRequisition,
  useCancelPurchaseRequisition,
  useOrdersLineItems,
} from '@/hooks';
import { usePageTitle } from '@/hooks/usePageTitle';
import {
  CreateOrderModal,
  type CreateOrderFormData,
} from '@/components/purchase-requisitions/CreateOrderModal';
import { ReceiveOrderModal } from '@/components/purchase-requisitions/ReceiveOrderModal';
import { CancelOrderConfirmationDialog } from '@/components/purchase-requisitions/CancelOrderConfirmationDialog';
import { EditPurchaseRequisitionModal } from '@/components/purchase-requisitions/EditPurchaseRequisitionModal';
import { ExpandedOrderContent } from '@/components/purchase-requisitions/ExpandedOrderContent';
import {
  PriceUpdateConfirmationModal,
  type PriceChange,
} from '@/components/purchase-requisitions/PriceUpdateConfirmationModal';
import { getCurrentFiscalYear, getFiscalYear, isInCurrentFiscalYear } from '@/utils/fiscalYear';
import { openBase64PdfInWindow, openDocumentWithAuth, preOpenWindow } from '@/utils/pdfHelpers';
import { documentService } from '@/services/documents';
import { formatCurrency } from '@/utils/currency';
import { useNotifications } from '@/stores/useNotificationStore';
import { logger } from '@equip/utils/logger';
import { formatNomenclatureWithSize } from '@equip/utils';
import type { OrderLineItemRow } from '@/services/ordersService';
import { ordersService, calculateAveragePrices } from '@/services/ordersService';
import { entityService } from '@/services/EntityService';
import { queryKeys } from '@/lib/queryKeys';
import { ORDER_STATUS_FILTERS } from '@/constants/orderConstants';
import { useWriteGuard } from '@/hooks/useWriteGuard';
import { DocumentFieldsModal } from '@/components/documents';
import type { DocumentPreviewResponse } from '@/services/documents';

type OrderStatus = 'Pending' | 'Partially Received' | 'Completed' | 'Cancelled';

type FiscalYearFilter = 'current' | 'all' | string;
type OrderStatusFilter =
  | 'all'
  | 'pending'
  | 'pending_action_facility'
  | 'pending_xo'
  | 'pending_cdd'
  | 'pending_ric'
  | 'on_order'
  | 'partial'
  | 'completed'
  | 'contract_awarded'
  | 'natick_mipr'
  | 'cancelled';

type PurchaseTypeFilter = 'all' | 'gpc' | 'natick_mipr' | 'contract' | 'gpc_gsa' | 'milstrip';

type SortField =
  | 'logFormNumber'
  | 'description'
  | 'documentNumber'
  | 'purchaseType'
  | 'qtyOrdered'
  | 'grandTotal'
  | 'externalStatus'
  | 'statusDate'
  | 'ufr';

interface OrderEntityData {
  purchase_type?: string;
  ufr?: boolean;
  external_status?: string;
  description?: string;
  document_number?: string;
  log_form_number?: number | string;
  status_date?: string;
  [key: string]: unknown;
}

export type OrderRow = {
  id: string;
  entity_identifier: string;
  orderDate: string;
  totalItems?: number;
  status: OrderStatus;
  entity_data?: OrderEntityData;
  /** Pre-computed outstanding quantity from orders_view (AB#29102) */
  outstandingQuantity?: number | null;
  /** Pre-computed total ordered quantity from orders_view (AB#29102) */
  totalOrderedQuantity?: number | null;
  /** Pre-computed total received quantity from orders_view */
  totalReceivedQuantity?: number | null;
};

/**
 * Calculates the total cost for an order based on its line items.
 * Sum of (quantity * actual_price) for each line item.
 * Falls back to unit_price_at_time_of_order if actual_price is not set.
 */
const calculateOrderTotal = (lineItems: OrderLineItemRow[] | undefined): number => {
  if (!lineItems || lineItems.length === 0) {
    return 0;
  }
  return lineItems.reduce((total, item) => {
    const quantity = item.entity_data.quantity ?? 0;
    const unitPrice = item.entity_data.unit_price_at_time_of_order ?? 0;
    const actualPrice = item.entity_data.actual_price ?? unitPrice;
    return total + quantity * actualPrice;
  }, 0);
};

/**
 * Calculates the total ordered quantity for an order.
 * Sum of line item quantities.
 */
const calculateTotalItems = (lineItems: OrderLineItemRow[] | undefined): number => {
  if (!lineItems || lineItems.length === 0) {
    return 0;
  }
  return lineItems.reduce((total, item) => total + (item.entity_data.quantity ?? 0), 0);
};

/**
 * Memoized order table row component for performance optimization
 * Prevents unnecessary re-renders when other rows change
 */
const OrderTableRow = React.memo(
  ({
    order,
    index,
    lineItemsByOrderId,
    isLoadingLineItems,
    onOpenOrderDetails,
    onOpenReceive,
    onOpenEdit,
    onOpenCancelDialog,
    cancelIsPending,
    isViewer,
  }: {
    order: OrderRow;
    index: number;
    lineItemsByOrderId: Map<string, OrderLineItemRow[]>;
    isLoadingLineItems: boolean;
    onOpenOrderDetails: (order: OrderRow) => void;
    onOpenReceive: (order: OrderRow) => void;
    onOpenEdit: (order: OrderRow) => void;
    onOpenCancelDialog: (order: OrderRow) => void;
    cancelIsPending: boolean;
    isViewer: boolean;
  }) => {
    const lineItems = lineItemsByOrderId.get(order.id) || [];
    const orderTotal = calculateOrderTotal(lineItems);
    const computedTotalItems = calculateTotalItems(lineItems);
    const displayedTotalItems =
      order.totalItems !== undefined ? order.totalItems : computedTotalItems;

    // Derive whether the order still has unreceived items.
    // Primary source: pre-computed outstanding_quantity from orders_view (fast, reliable).
    // Fallback: line item receipt data (handles cache race during loading).
    // If entity_data.status was prematurely set to "Completed" but items remain,
    // the order is not actually completed — treat it as "Partially Received" (AB#29102).
    const hasOutstandingFromView =
      order.outstandingQuantity != null && order.totalOrderedQuantity != null
        ? order.totalOrderedQuantity > 0 && order.outstandingQuantity > 0
        : null;
    const hasOutstandingFromLineItems =
      lineItems.length > 0
        ? lineItems.some((item) => {
            const ordered = item.entity_data.quantity ?? 0;
            const received =
              item.receipts?.reduce(
                (sum, receipt) => sum + (receipt.entity_data?.quantity_received ?? 0),
                0,
              ) ?? 0;
            return received < ordered;
          })
        : null;
    const hasOutstandingItems =
      hasOutstandingFromView === true || hasOutstandingFromLineItems === true;
    const effectiveStatus: OrderStatus =
      order.status === 'Completed' && hasOutstandingItems ? 'Partially Received' : order.status;

    const externalStatus = order.entity_data?.external_status || '-';
    const purchaseType = order.entity_data?.purchase_type || '-';
    const description = order.entity_data?.description || '-';
    const documentNumber = order.entity_data?.document_number || '-';
    const logFormNumber = order.entity_data?.log_form_number
      ? String(order.entity_data.log_form_number).padStart(6, '0')
      : '-';
    const ufr = order.entity_data?.ufr || false;
    const statusDate = order.entity_data?.status_date
      ? new Date(order.entity_data.status_date).toLocaleDateString('en-US')
      : new Date(order.orderDate).toLocaleDateString('en-US');

    return (
      <TableRow
        hover
        sx={{
          bgcolor: (t) =>
            index % 2 === 0
              ? 'background.paper'
              : t.palette.mode === 'dark'
                ? 'rgba(255,255,255,0.03)'
                : 'grey.50',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'action.hover',
          },
        }}
        onClick={() => onOpenOrderDetails(order)}
        role="button"
        tabIndex={0}
        aria-label={`View details for order ${logFormNumber}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenOrderDetails(order);
          }
        }}
      >
        <TableCell sx={{ fontSize: '13px', fontWeight: 400 }}>{logFormNumber}</TableCell>
        <TableCell
          sx={{
            fontSize: '13px',
            fontWeight: 400,
            maxWidth: 200,
            lineHeight: '1.4',
          }}
        >
          {description}
        </TableCell>
        <TableCell sx={{ fontSize: '13px', fontWeight: 400 }}>{documentNumber}</TableCell>
        <TableCell sx={{ fontSize: '13px', fontWeight: 400 }}>{purchaseType}</TableCell>
        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 400 }}>
          {isLoadingLineItems && order.totalItems === undefined ? '...' : displayedTotalItems}
        </TableCell>
        <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 700 }}>
          {isLoadingLineItems ? '...' : formatCurrency(orderTotal)}
        </TableCell>
        <TableCell sx={{ fontSize: '13px', fontWeight: 400 }}>{externalStatus}</TableCell>
        <TableCell sx={{ fontSize: '13px', fontWeight: 400 }}>{statusDate}</TableCell>
        <TableCell align="center">
          <Box
            sx={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              bgcolor: (t) =>
                ufr ? 'grey.700' : t.palette.mode === 'dark' ? 'grey.600' : 'grey.400',
              color: 'common.white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 500,
              margin: '0 auto',
            }}
          >
            {ufr ? 'Y' : 'N'}
          </Box>
        </TableCell>
        <TableCell align="center" onClick={(e) => e.stopPropagation()}>
          {!isViewer && effectiveStatus !== 'Cancelled' && effectiveStatus !== 'Completed' ? (
            <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
              <Tooltip
                title={!hasOutstandingItems ? 'All items have been received' : 'Edit Order'}
                arrow
              >
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: 'primary.main', padding: '4px' }}
                    onClick={() => onOpenEdit(order)}
                    disabled={
                      (effectiveStatus !== 'Pending' && effectiveStatus !== 'Partially Received') ||
                      !hasOutstandingItems
                    }
                    aria-label="Edit Order"
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Receive Items" arrow>
                <IconButton
                  size="small"
                  sx={{ color: 'secondary.main', padding: '4px' }}
                  onClick={() => onOpenReceive(order)}
                  aria-label="Receive Items"
                >
                  <InboxIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cancel Order" arrow>
                <span>
                  <IconButton
                    size="small"
                    sx={{ color: 'error.main', padding: '4px' }}
                    onClick={() => onOpenCancelDialog(order)}
                    disabled={effectiveStatus !== 'Pending' || cancelIsPending}
                    aria-label="Cancel Order"
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              -
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  },
);

OrderTableRow.displayName = 'OrderTableRow';

const log = logger.child({ component: 'PurchaseRequisitionsPage' });

export const PurchaseRequisitionsPage: React.FC = () => {
  usePageTitle('Purchase Requisitions');
  const navigate = useNavigate();
  const notifications = useNotifications();
  const isViewer = useWriteGuard();

  // Default to current fiscal year per ticket #28095
  const [fiscalYearFilter, setFiscalYearFilter] = useState<FiscalYearFilter>('current');
  const [orderStatusFilter, setOrderStatusFilter] = useState<OrderStatusFilter>('all');
  const [purchaseTypeFilter, setPurchaseTypeFilter] = useState<PurchaseTypeFilter>('all');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [dateTo, setDateTo] = useState<Dayjs | null>(null);
  const dateRangeInvalid = Boolean(
    dateFrom?.isValid() && dateTo?.isValid() && dateFrom.isAfter(dateTo, 'day'),
  );
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [receiveOrder, setReceiveOrder] = useState<OrderRow | null>(null);
  const [editOrder, setEditOrder] = useState<OrderRow | null>(null);
  const [cancelOrder, setCancelOrder] = useState<OrderRow | null>(null);
  const [cancellationReason, setCancellationReason] = useState('');

  // Document fields modal state (pre-fill popup before PDF generation)
  const [docFieldsModalOpen, setDocFieldsModalOpen] = useState(false);
  const [docPreviewData, setDocPreviewData] = useState<DocumentPreviewResponse | null>(null);
  const [isGeneratingDoc, setIsGeneratingDoc] = useState(false);
  const pendingResultRef = useRef<{ transactionId?: string } | null>(null);

  // Price update confirmation modal state
  const [priceUpdateModalOpen, setPriceUpdateModalOpen] = useState(false);
  const [pendingPriceChanges, setPendingPriceChanges] = useState<PriceChange[]>([]);
  const [pendingItems, setPendingItems] = useState<
    Array<{ catalogId: string; quantity: number; actualPrice?: number }>
  >([]);
  const [pendingFormData, setPendingFormData] = useState<CreateOrderFormData | null>(null);

  // Sorting state
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isExporting, setIsExporting] = useState(false);

  const completePurchaseRequisition = useCompletePurchaseRequisition();
  const cancelPurchaseRequisition = useCancelPurchaseRequisition();
  const queryClient = useQueryClient();

  const ordersQuery = useOrders();
  const mappedOrders = useMemo<OrderRow[]>(
    () =>
      (ordersQuery.data || []).map((order) => ({
        id: order.id,
        entity_identifier: order.entity_identifier ?? `PO-${order.id.slice(0, 8)}`,
        orderDate: order.entity_data.order_date ?? new Date().toISOString(),
        totalItems:
          order.metadata?.aggregates?.total_item_quantity ??
          order.entity_data.total_items ??
          undefined,
        status: (order.entity_data.status ?? 'Pending') as OrderStatus,
        entity_data: order.entity_data,
        outstandingQuantity: order.outstanding_quantity ?? null,
        totalOrderedQuantity: order.total_ordered_quantity ?? null,
        totalReceivedQuantity: order.total_received_quantity ?? null,
      })),
    [ordersQuery.data],
  );

  const catalogNonBuyQuery = useCatalogNonBuy();
  const catalogItems = useMemo(
    () =>
      (catalogNonBuyQuery.data || []).map((item) => ({
        id: item.id,
        name: item.display_name,
        nsn: item.entity_data.nsn,
        lin: item.entity_data.lin,
        unitPrice: item.entity_data.unit_price,
        entity_data: item.entity_data,
        size: (item.entity_data as Record<string, unknown>).size as string | undefined,
      })),
    [catalogNonBuyQuery.data],
  );

  // Calculate available fiscal years from order data
  const availableFiscalYears = useMemo(() => {
    const yearsSet = new Set<number>();
    mappedOrders.forEach((order) => {
      const fy = getFiscalYear(new Date(order.orderDate));
      yearsSet.add(fy);
    });
    return Array.from(yearsSet).sort((a, b) => b - a); // Descending order
  }, [mappedOrders]);

  // Apply filters to get the visible orders
  const filteredOrders = useMemo(() => {
    let result = mappedOrders;

    // Apply fiscal year filter
    if (fiscalYearFilter === 'current') {
      result = result.filter((order) => isInCurrentFiscalYear(new Date(order.orderDate)));
    } else if (fiscalYearFilter !== 'all') {
      // Filter by specific fiscal year
      const targetYear = Number.parseInt(fiscalYearFilter, 10);
      if (!Number.isNaN(targetYear)) {
        result = result.filter((order) => getFiscalYear(new Date(order.orderDate)) === targetYear);
      }
    }

    // Apply external status filter
    const externalStatusMatch = (order: OrderRow, filter: string): boolean => {
      const externalStatus = order.entity_data?.external_status;
      // Null safety: ensure externalStatus is a string before calling toLowerCase
      if (!externalStatus || typeof externalStatus !== 'string') {
        return filter === 'all';
      }
      const statusLower = externalStatus.toLowerCase();

      switch (filter) {
        case 'pending':
          return (
            statusLower === 'pending' ||
            (statusLower.includes('pending') && !statusLower.includes('pending action'))
          );
        case 'pending_action_facility':
          return statusLower.includes('pending action facility');
        case 'pending_xo':
          return statusLower.includes('pending xo');
        case 'pending_cdd':
          return statusLower.includes('pending cdd');
        case 'pending_ric':
          return statusLower.includes('pending ric');
        case 'on_order':
          return statusLower.includes('on order');
        case 'partial':
          return statusLower.includes('partial');
        case 'completed':
          return statusLower.includes('completed') || statusLower.includes('received');
        case 'contract_awarded':
          return statusLower.includes('contract awarded');
        case 'natick_mipr':
          return statusLower.includes('natick') || statusLower.includes('mipr');
        case 'cancelled':
          return statusLower.includes('cancelled');
        default:
          return true;
      }
    };

    if (orderStatusFilter !== 'all') {
      result = result.filter((order) => externalStatusMatch(order, orderStatusFilter));
    }

    // Apply purchase type filter
    if (purchaseTypeFilter !== 'all') {
      result = result.filter((order) => {
        const purchaseType = order.entity_data?.purchase_type;
        // Null safety: handle undefined/null purchase types
        if (!purchaseType || typeof purchaseType !== 'string') {
          return false;
        }
        const purchaseTypeLower = purchaseType.toLowerCase();

        switch (purchaseTypeFilter) {
          case 'gpc':
            return purchaseTypeLower === 'gpc';
          case 'natick_mipr':
            return purchaseTypeLower.includes('natick') || purchaseTypeLower.includes('mipr');
          case 'contract':
            return purchaseTypeLower === 'contract';
          case 'gpc_gsa':
            return purchaseTypeLower.includes('gpc') && purchaseTypeLower.includes('gsa');
          case 'milstrip':
            return purchaseTypeLower === 'milstrip';
          default:
            return true;
        }
      });
    }

    // Apply date range filter
    if (dateRangeInvalid) return [];

    const from = dateFrom ? dateFrom.startOf('day').valueOf() : null;
    const to = dateTo ? dateTo.endOf('day').valueOf() : null;

    if (from !== null || to !== null) {
      result = result.filter((order) => {
        const orderDate = dayjs(order.orderDate);
        if (!orderDate.isValid()) {
          return false;
        }
        const orderDateValue = orderDate.valueOf();
        if (from !== null && orderDateValue < from) {
          return false;
        }
        if (to !== null && orderDateValue > to) {
          return false;
        }
        return true;
      });
    }

    return result;
  }, [
    mappedOrders,
    fiscalYearFilter,
    orderStatusFilter,
    purchaseTypeFilter,
    dateFrom,
    dateTo,
    dateRangeInvalid,
  ]);

  // Load line items for all filtered orders when sorting by grand total (AB#29362)
  const allFilteredOrderIds = useMemo(
    () => (sortField === 'grandTotal' ? filteredOrders.map((order) => order.id) : []),
    [sortField, filteredOrders],
  );
  const { lineItemsByOrderId: sortLineItemsByOrderId } = useOrdersLineItems(allFilteredOrderIds);
  const orderTotalsMap = useMemo(() => {
    const map = new Map<string, number>();
    sortLineItemsByOrderId.forEach((lineItems, orderId) => {
      map.set(orderId, calculateOrderTotal(lineItems));
    });
    return map;
  }, [sortLineItemsByOrderId]);

  // Apply sorting to filtered orders
  const sortedOrders = useMemo(() => {
    if (!sortField) return filteredOrders;
    return [...filteredOrders].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      switch (sortField) {
        case 'logFormNumber':
          aVal =
            a.entity_data?.log_form_number !== undefined
              ? Number(a.entity_data.log_form_number)
              : -1;
          bVal =
            b.entity_data?.log_form_number !== undefined
              ? Number(b.entity_data.log_form_number)
              : -1;
          break;
        case 'description':
          aVal = (a.entity_data?.description ?? '').toLowerCase();
          bVal = (b.entity_data?.description ?? '').toLowerCase();
          break;
        case 'documentNumber':
          aVal = (a.entity_data?.document_number ?? '').toLowerCase();
          bVal = (b.entity_data?.document_number ?? '').toLowerCase();
          break;
        case 'purchaseType':
          aVal = (a.entity_data?.purchase_type ?? '').toLowerCase();
          bVal = (b.entity_data?.purchase_type ?? '').toLowerCase();
          break;
        case 'qtyOrdered':
          aVal = Number(a.totalItems ?? 0);
          bVal = Number(b.totalItems ?? 0);
          break;
        case 'grandTotal':
          aVal = orderTotalsMap.get(a.id) ?? 0;
          bVal = orderTotalsMap.get(b.id) ?? 0;
          break;
        case 'externalStatus':
          aVal = (a.entity_data?.external_status ?? '').toLowerCase();
          bVal = (b.entity_data?.external_status ?? '').toLowerCase();
          break;
        case 'statusDate':
          aVal = a.entity_data?.status_date ?? a.orderDate;
          bVal = b.entity_data?.status_date ?? b.orderDate;
          break;
        case 'ufr':
          aVal = a.entity_data?.ufr ? 1 : 0;
          bVal = b.entity_data?.ufr ? 1 : 0;
          break;
        default:
          return 0;
      }
      if (aVal === bVal) return 0;
      const result = aVal < bVal ? -1 : 1;
      return sortDirection === 'asc' ? result : -result;
    });
  }, [filteredOrders, sortField, sortDirection, orderTotalsMap]);

  // Apply pagination to sorted orders
  const paginatedOrders = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedOrders.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedOrders, page, rowsPerPage]);

  // Reset to first page when filters change
  const prevFilteredLength = useRef(filteredOrders.length);
  useEffect(() => {
    if (prevFilteredLength.current !== filteredOrders.length) {
      setPage(0);
      prevFilteredLength.current = filteredOrders.length;
    }
  }, [filteredOrders.length]);

  // Eager load line items for paginated orders + selected order (AB#28093)
  const orderIds = useMemo(() => {
    const ids = paginatedOrders.map((order) => order.id);
    // Ensure selected order is included even if not in paginated list
    if (selectedOrderId && !ids.includes(selectedOrderId)) {
      ids.push(selectedOrderId);
    }
    return ids;
  }, [paginatedOrders, selectedOrderId]);
  const { lineItemsByOrderId, isLoading: isLoadingLineItems } = useOrdersLineItems(orderIds);

  // Get selected order from the orders list (always fresh from query)
  const selectedOrder = useMemo(
    () => (selectedOrderId ? mappedOrders.find((o) => o.id === selectedOrderId) || null : null),
    [selectedOrderId, mappedOrders],
  );

  // Performance optimization: memoize selected order line items lookup
  const selectedOrderLineItems = useMemo(
    () => (selectedOrder ? lineItemsByOrderId.get(selectedOrder.id) || [] : []),
    [selectedOrder, lineItemsByOrderId],
  );

  const handleOpenCreate = useCallback(() => {
    setModalOpen(false);
    setCreateError(null);
    setOpenCreate(true);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setOpenCreate(false);
    setCreateError(null);
  }, []);

  /**
   * Core logic for creating a PR once price decisions have been made.
   * Extracted so it can be called from both the direct path (no diffs) and from
   * the modal callbacks ("Accept & Create" / "Skip & Create").
   */
  const executeCreateRequisition = useCallback(
    async (
      items: Array<{ catalogId: string; quantity: number; actualPrice?: number }>,
      formData: CreateOrderFormData,
    ) => {
      setCreateError(null);

      try {
        const result = await completePurchaseRequisition.mutateAsync({
          items,
          purchaseType: (formData.purchaseType || undefined) as
            | 'GPC'
            | 'Natick/MIPR'
            | 'Contract'
            | 'GPC/GSA 25K'
            | 'MILSTRIP'
            | undefined,
          description: formData.description || undefined,
          ufr: formData.ufr,
          logFormNumber: Number.parseInt(formData.logFormNumber, 10),
          assetAuthority: formData.assetAuthority || undefined,
          priority: formData.priority || undefined,
          vendor: formData.vendor || undefined,
          expectedDeliveryDate: formData.expectedDeliveryDate || undefined,
          requiredByDate: formData.requiredByDate || undefined,
          fmz: formData.fmz || undefined,
          wbsCc: formData.wbsCc || undefined,
        });
        setOpenCreate(false);
        setPriceUpdateModalOpen(false);

        // Build initial document payload
        const pdfItems = items.map((cartItem, index) => {
          const catalog = catalogItems.find((c) => c.id === cartItem.catalogId);
          const qty = cartItem.quantity;
          const unitCost = cartItem.actualPrice ?? catalog?.unitPrice ?? 0;
          return {
            line_number: String(index + 1),
            nomenclature: formatNomenclatureWithSize(catalog?.name, catalog?.size) || 'Unknown',
            nsn: catalog?.nsn ?? '',
            lin: catalog?.lin ?? '',
            quantity: String(qty),
            unit_cost: formatCurrency(unitCost),
            total_cost: formatCurrency(qty * unitCost),
          };
        });

        const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
        const totalCost = items.reduce((sum, cartItem) => {
          const catalog = catalogItems.find((c) => c.id === cartItem.catalogId);
          const unitCost = cartItem.actualPrice ?? catalog?.unitPrice ?? 0;
          return sum + cartItem.quantity * unitCost;
        }, 0);

        const initialPayload: Record<string, unknown> = {
          document_number: result.documentNumber ?? result.orderIdentifier,
          order_date: new Date().toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
          }),
          order_status: 'Pending',
          purchase_type: 'Non-Expendable',
          total_qty: String(totalQty),
          grand_total: formatCurrency(totalCost),
          items: pdfItems,
          // Additional fields — editable in the preview popup per mapping
          pr_description: formData.description || '',
          asset_authority: formData.assetAuthority || '',
          priority: formData.priority || '',
          vendor: formData.vendor || '',
          expected_delivery_date: formData.expectedDeliveryDate || '',
          required_by_date: formData.requiredByDate || '',
          fmz: formData.fmz || '',
          wbscc: formData.wbsCc || '',
        };

        // Store result for use in the confirm callback
        pendingResultRef.current = result;

        // Fetch document preview to show editable fields popup
        try {
          const preview = await documentService.getDocumentPreview({
            documentType: 'AssetAcquisitionOrder',
            payload: initialPayload,
          });
          setDocPreviewData(preview);
          setDocFieldsModalOpen(true);
        } catch (previewError) {
          log.warn('Document preview unavailable', {
            error: String(previewError),
          });
          pendingResultRef.current = null;
          notifications.warning(
            'Document Notice',
            'Order created, but the document form could not be loaded. You can regenerate the document later.',
          );
        }
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to create purchase requisition';
        setCreateError(message);
        throw error;
      }
    },
    [completePurchaseRequisition, catalogItems, notifications],
  );

  const handleDocFieldsConfirm = useCallback(
    async (editedPayload: Record<string, unknown>) => {
      const docWindow = preOpenWindow();
      setIsGeneratingDoc(true);

      try {
        const docResult = await documentService.generateDocument({
          documentType: 'AssetAcquisitionOrder',
          payload: editedPayload,
          ...(pendingResultRef.current?.transactionId
            ? { options: { metadata: { transactionId: pendingResultRef.current.transactionId } } }
            : {}),
        });

        if (docResult.outputBuffer) {
          const opened = openBase64PdfInWindow(docResult.outputBuffer, docWindow);
          if (!opened && docResult.downloadUrl) {
            // Inline open failed — fall back to authenticated download
            const fallbackUrl = documentService.resolveDownloadUrl(docResult.downloadUrl);
            if (fallbackUrl) await openDocumentWithAuth(fallbackUrl);
          }
        } else if (docResult.downloadUrl) {
          const url = documentService.resolveDownloadUrl(docResult.downloadUrl);
          if (url) {
            await openDocumentWithAuth(url, docWindow);
          } else if (docWindow && !docWindow.closed) {
            docWindow.close();
          }
        } else if (docWindow && !docWindow.closed) {
          docWindow.close();
        }

        // Link document to transaction
        const pendingResult = pendingResultRef.current;
        if (docResult.documentEntityId && pendingResult?.transactionId) {
          try {
            await documentService.linkDocumentToTransaction(
              docResult.documentEntityId,
              pendingResult.transactionId,
            );
          } catch (linkError) {
            log.error('Failed to link Asset Acquisition Order Form to transaction', {
              error: String(linkError),
            });
          }
        }
        notifications.success(
          'Document Generated',
          'Asset Acquisition Order form generated successfully.',
        );
      } catch (docError) {
        if (docWindow && !docWindow.closed) docWindow.close();
        log.error('Failed to generate Asset Acquisition Order Form', {
          error: String(docError),
        });
        notifications.warning(
          'Document Notice',
          'The Asset Acquisition Order Form could not be generated.',
        );
      } finally {
        setIsGeneratingDoc(false);
        setDocFieldsModalOpen(false);
        setDocPreviewData(null);
        pendingResultRef.current = null;
      }
    },
    [notifications],
  );

  const handleDocFieldsDismiss = useCallback(() => {
    setDocFieldsModalOpen(false);
    setDocPreviewData(null);
    pendingResultRef.current = null;
  }, []);

  const handleCreateSubmit = useCallback(
    async (
      items: Array<{ catalogId: string; quantity: number; actualPrice?: number }>,
      formData: CreateOrderFormData,
    ) => {
      setCreateError(null);

      // Look up 365-day price history for the cart's catalog IDs
      const catalogIds = [...new Set(items.map((i) => i.catalogId))];
      const since = new Date(Date.now() - 365 * 24 * 60 * 60 * 1000);
      let priceChanges: PriceChange[] = [];

      try {
        const lineItems = await ordersService.getOrderLineItemsByCatalogIds(catalogIds, since);
        const avgPrices = calculateAveragePrices(lineItems);

        priceChanges = items
          .map((cartItem) => {
            const catalog = catalogItems.find((c) => c.id === cartItem.catalogId);
            if (!catalog) return null;
            const currentPrice = Number(catalog.unitPrice) || 0;
            const avgResult = avgPrices.get(cartItem.catalogId);

            // Use actual_price entered by the user if available (for current cart item),
            // otherwise fall back to catalog price for comparison.
            const effectiveActualPrice = cartItem.actualPrice ?? currentPrice;

            // Include the current cart price in the quantity-weighted average so
            // the proposed price reflects ALL data points (history + this transaction).
            const cartQty = cartItem.quantity > 0 ? cartItem.quantity : 1;
            let proposedPrice: number;
            if (avgResult) {
              const totalSum =
                avgResult.avgPrice * avgResult.sampleCount + effectiveActualPrice * cartQty;
              const totalCount = avgResult.sampleCount + cartQty;
              proposedPrice = Math.round((totalSum / totalCount) * 100) / 100;
            } else {
              proposedPrice = Math.round(effectiveActualPrice * 100) / 100;
            }

            // Only propose a change if the computed price differs from current
            if (Math.abs(proposedPrice - currentPrice) < 0.005) return null;

            return {
              catalogId: cartItem.catalogId,
              name: catalog.name ?? 'Unknown',
              nsn: catalog.nsn ?? '',
              currentPrice,
              proposedPrice,
              sampleCount: (avgResult?.sampleCount ?? 0) + cartQty,
            } satisfies PriceChange;
          })
          .filter((c): c is PriceChange => c !== null);

        // Deduplicate by catalogId (multiple cart items can share a catalog)
        const seen = new Set<string>();
        priceChanges = priceChanges.filter((c) => {
          if (seen.has(c.catalogId)) return false;
          seen.add(c.catalogId);
          return true;
        });
      } catch (lookupError) {
        log.error('Failed to look up price history; skipping price update modal', {
          error: String(lookupError),
        });
      }

      if (priceChanges.length > 0) {
        // Show confirmation modal — store pending submission data
        setPendingPriceChanges(priceChanges);
        setPendingItems(items);
        setPendingFormData(formData);
        setPriceUpdateModalOpen(true);
        return;
      }

      // No diffs — proceed directly
      await executeCreateRequisition(items, formData);
    },
    [catalogItems, executeCreateRequisition],
  );

  const handlePriceModalAccept = useCallback(async () => {
    if (!pendingFormData) return;

    // Update catalog prices in parallel
    try {
      await Promise.all(
        pendingPriceChanges.map((change) => {
          const catalogItem = catalogItems.find((item) => item.id === change.catalogId);
          return entityService.update({
            type: 'catalog',
            id: change.catalogId,
            patch: {
              entity_data: {
                ...catalogItem?.entity_data,
                unit_price: change.proposedPrice,
                unit_price_source: 'calculated',
                unit_price_last_updated: new Date().toISOString(),
              },
            },
          });
        }),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.catalog.all });
    } catch (updateError) {
      log.error('Failed to update catalog prices', { error: String(updateError) });
      notifications.warning(
        'Price Update',
        'Could not update catalog prices, but will still create the requisition.',
      );
    }

    await executeCreateRequisition(pendingItems, pendingFormData);
  }, [
    pendingPriceChanges,
    pendingItems,
    pendingFormData,
    executeCreateRequisition,
    queryClient,
    notifications,
    catalogItems,
  ]);

  const handlePriceModalSkip = useCallback(async () => {
    if (!pendingFormData) return;
    await executeCreateRequisition(pendingItems, pendingFormData);
  }, [pendingItems, pendingFormData, executeCreateRequisition]);

  const handlePriceModalCancel = useCallback(() => {
    setPriceUpdateModalOpen(false);
    setPendingPriceChanges([]);
    setPendingItems([]);
    setPendingFormData(null);
  }, []);

  const handleOpenReceive = useCallback((order: OrderRow) => {
    setReceiveOrder(order);
  }, []);

  const handleCloseReceive = useCallback(() => {
    setReceiveOrder(null);
  }, []);

  const handleOpenEdit = useCallback((order: OrderRow) => {
    setEditOrder(order);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditOrder(null);
  }, []);

  const handleOpenCancelDialog = useCallback((order: OrderRow) => {
    setCancelOrder(order);
    setCancellationReason('');
  }, []);

  const handleCloseCancelDialog = useCallback(() => {
    setCancelOrder(null);
    setCancellationReason('');
  }, []);

  const handleConfirmCancel = useCallback(async () => {
    if (!cancelOrder) return;
    const trimmedCancellationReason = cancellationReason.trim();
    if (!trimmedCancellationReason) return;
    await cancelPurchaseRequisition.mutateAsync({
      orderId: cancelOrder.id,
      cancellationReason: trimmedCancellationReason,
    });
    setCancelOrder(null);
    setCancellationReason('');
  }, [cancelOrder, cancellationReason, cancelPurchaseRequisition]);

  const handleOpenOrderDetails = useCallback((order: OrderRow) => {
    setSelectedOrderId(order.id);
    setModalOpen(true);
  }, []);

  const handleCloseOrderDetails = useCallback(() => {
    setModalOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
      setPage(0);
    },
    [sortField],
  );

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleExportXlsx = useCallback(async () => {
    setIsExporting(true);

    try {
      const exportLineItemsByOrderId = new Map(lineItemsByOrderId);
      const missingOrderIds = sortedOrders
        .map((order) => order.id)
        .filter((orderId) => !exportLineItemsByOrderId.has(orderId));

      if (missingOrderIds.length > 0) {
        const fetched = await Promise.all(
          missingOrderIds.map(async (orderId) => {
            const lineItems = await ordersService.getOrderLineItems(orderId);
            return { orderId, lineItems };
          }),
        );

        fetched.forEach(({ orderId, lineItems }) => {
          exportLineItemsByOrderId.set(orderId, lineItems);
        });
      }

      const headers = [
        'Log Form #',
        'Description',
        'Document Number',
        'Purchase Type',
        'QTY Ordered',
        'Received Quantity',
        'Grand Total',
        'External Status',
        'Status Date',
        'UFR',
      ];

      const rows = sortedOrders.map((order) => {
        const lineItems = exportLineItemsByOrderId.get(order.id) ?? [];
        const logFormNumber = order.entity_data?.log_form_number
          ? String(order.entity_data.log_form_number).padStart(6, '0')
          : '-';
        const description = order.entity_data?.description || '-';
        const documentNumber = order.entity_data?.document_number || '-';
        const purchaseType = order.entity_data?.purchase_type || '-';
        const qtyOrdered =
          order.totalOrderedQuantity ?? order.totalItems ?? calculateTotalItems(lineItems);
        const receivedQty =
          order.totalReceivedQuantity ??
          lineItems.reduce(
            (sum, item) =>
              sum +
              (item.receipts?.reduce(
                (receiptSum, receipt) => receiptSum + (receipt.entity_data?.quantity_received ?? 0),
                0,
              ) ?? 0),
            0,
          );
        const grandTotal = calculateOrderTotal(lineItems);
        const externalStatus = order.entity_data?.external_status || '-';
        const statusDate = order.entity_data?.status_date
          ? new Date(order.entity_data.status_date).toLocaleDateString('en-US')
          : new Date(order.orderDate).toLocaleDateString('en-US');
        const ufr = order.entity_data?.ufr ? 'Y' : 'N';

        return [
          logFormNumber,
          description,
          documentNumber,
          purchaseType,
          qtyOrdered,
          receivedQty,
          grandTotal,
          externalStatus,
          statusDate,
          ufr,
        ];
      });

      const worksheet = utils.aoa_to_sheet([headers, ...rows]);
      const workbook = utils.book_new();
      utils.book_append_sheet(workbook, worksheet, 'Purchase Requisitions');

      const buffer = write(workbook, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = URL.createObjectURL(blob);
      const datePart = new Date().toISOString().split('T')[0];
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase-requisitions-${datePart}.xlsx`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 60000);

      notifications.success('Export complete', 'Purchase requisitions exported to XLSX.');
    } catch (error) {
      log.error('Failed to export purchase requisitions', { error: String(error) });
      notifications.error('Export failed', 'Could not export purchase requisitions to XLSX.');
    } finally {
      setIsExporting(false);
    }
  }, [lineItemsByOrderId, sortedOrders, notifications]);

  return (
    <Container maxWidth={false} sx={{ mt: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">Purchase Requisitions</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ShoppingCart color="primary" fontSize="large" />
          <Typography variant="h4" component="h1">
            Purchase Requisitions
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="fiscal-year-filter-label">Fiscal Year</InputLabel>
            <Select
              labelId="fiscal-year-filter-label"
              id="fiscal-year-filter"
              value={fiscalYearFilter}
              label="Fiscal Year"
              onChange={(e) => setFiscalYearFilter(e.target.value as FiscalYearFilter)}
            >
              <MenuItem value="current">Current FY ({getCurrentFiscalYear()})</MenuItem>
              {availableFiscalYears.map((year) => (
                <MenuItem key={year} value={String(year)}>
                  FY {year}
                </MenuItem>
              ))}
              <MenuItem value="all">All Fiscal Years</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="order-status-filter-label">External Status</InputLabel>
            <Select
              labelId="order-status-filter-label"
              id="order-status-filter"
              value={orderStatusFilter}
              label="External Status"
              onChange={(e) => setOrderStatusFilter(e.target.value as OrderStatusFilter)}
            >
              {ORDER_STATUS_FILTERS.map((filter) => (
                <MenuItem key={filter.value} value={filter.value}>
                  {filter.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="purchase-type-filter-label">Purchase Type</InputLabel>
            <Select
              labelId="purchase-type-filter-label"
              id="purchase-type-filter"
              value={purchaseTypeFilter}
              label="Purchase Type"
              onChange={(e) => setPurchaseTypeFilter(e.target.value as PurchaseTypeFilter)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="gpc">GPC</MenuItem>
              <MenuItem value="natick_mipr">Natick/MIPR</MenuItem>
              <MenuItem value="contract">Contract</MenuItem>
              <MenuItem value="gpc_gsa">GPC/GSA 25K</MenuItem>
              <MenuItem value="milstrip">MILSTRIP</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date From"
              value={dateFrom}
              onChange={(value) => setDateFrom(value?.isValid() ? value : null)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 180 },
                },
              }}
            />

            <DatePicker
              label="Date To"
              value={dateTo}
              onChange={(value) => setDateTo(value?.isValid() ? value : null)}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { minWidth: 180 },
                },
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={handleExportXlsx}
            disabled={isExporting || dateRangeInvalid || filteredOrders.length === 0}
          >
            {isExporting ? 'Exporting…' : 'Export XLSX'}
          </Button>
          {!isViewer && (
            <Button variant="contained" startIcon={<Add />} onClick={handleOpenCreate}>
              Create New Requisition
            </Button>
          )}
        </Box>
      </Box>

      {dateRangeInvalid && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Date From cannot be after Date To.
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table size="medium" aria-label="Orders">
          <TableHead>
            <TableRow
              sx={{ bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.800' : 'grey.300') }}
            >
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'logFormNumber'}
                  direction={sortField === 'logFormNumber' ? sortDirection : 'asc'}
                  onClick={() => handleSort('logFormNumber')}
                >
                  Log Form #
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'documentNumber'}
                  direction={sortField === 'documentNumber' ? sortDirection : 'asc'}
                  onClick={() => handleSort('documentNumber')}
                >
                  Document Number
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'purchaseType'}
                  direction={sortField === 'purchaseType' ? sortDirection : 'asc'}
                  onClick={() => handleSort('purchaseType')}
                >
                  Purchase Type
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                <TableSortLabel
                  active={sortField === 'qtyOrdered'}
                  direction={sortField === 'qtyOrdered' ? sortDirection : 'asc'}
                  onClick={() => handleSort('qtyOrdered')}
                  sx={{ flexDirection: 'row' }}
                >
                  QTY Ordered
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                <TableSortLabel
                  active={sortField === 'grandTotal'}
                  direction={sortField === 'grandTotal' ? sortDirection : 'asc'}
                  onClick={() => handleSort('grandTotal')}
                  sx={{ flexDirection: 'row' }}
                >
                  Grand Total
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'externalStatus'}
                  direction={sortField === 'externalStatus' ? sortDirection : 'asc'}
                  onClick={() => handleSort('externalStatus')}
                >
                  External Status
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'statusDate'}
                  direction={sortField === 'statusDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('statusDate')}
                >
                  Status Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                <TableSortLabel
                  active={sortField === 'ufr'}
                  direction={sortField === 'ufr' ? sortDirection : 'asc'}
                  onClick={() => handleSort('ufr')}
                  sx={{ justifyContent: 'center' }}
                >
                  UFR
                </TableSortLabel>
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontSize: '13px', fontWeight: 500, whiteSpace: 'nowrap' }}
              >
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {ordersQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={10}>Loading...</TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>No orders found</TableCell>
              </TableRow>
            ) : (
              paginatedOrders.map((order, index) => (
                <OrderTableRow
                  key={order.id}
                  order={order}
                  index={index}
                  lineItemsByOrderId={lineItemsByOrderId}
                  isLoadingLineItems={isLoadingLineItems}
                  onOpenOrderDetails={handleOpenOrderDetails}
                  onOpenReceive={handleOpenReceive}
                  onOpenEdit={handleOpenEdit}
                  onOpenCancelDialog={handleOpenCancelDialog}
                  cancelIsPending={cancelPurchaseRequisition.isPending}
                  isViewer={isViewer}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination controls */}
      <TablePagination
        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
        component="div"
        count={filteredOrders.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          borderTop: '1px solid rgba(224, 224, 224, 1)',
        }}
      />

      {/* Order Details Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseOrderDetails}
        maxWidth="lg"
        fullWidth
        disableEnforceFocus
        aria-labelledby="order-details-dialog-title"
        aria-describedby="order-details-dialog-description"
        PaperProps={{
          sx: {
            minHeight: '600px',
            maxHeight: '90vh',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 24px',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          {selectedOrder && (
            <Typography variant="h6" sx={{ fontWeight: 500 }} id="order-details-dialog-title">
              Log Form #{' '}
              {selectedOrder.entity_data?.log_form_number
                ? String(selectedOrder.entity_data.log_form_number).padStart(6, '0')
                : '-'}
            </Typography>
          )}
          <IconButton
            onClick={handleCloseOrderDetails}
            size="small"
            aria-label="Close order details"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: 0 }}>
          {selectedOrder && (
            <ExpandedOrderContent order={selectedOrder} lineItems={selectedOrderLineItems} />
          )}
        </DialogContent>
      </Dialog>

      <CreateOrderModal
        open={openCreate}
        onClose={handleCloseCreate}
        onSubmit={handleCreateSubmit}
        catalogItems={catalogItems}
        isLoadingCatalog={catalogNonBuyQuery.isLoading}
        isSubmitting={completePurchaseRequisition.isPending}
        errorMessage={createError}
      />
      <ReceiveOrderModal
        open={Boolean(receiveOrder)}
        onClose={handleCloseReceive}
        order={
          receiveOrder
            ? {
                id: receiveOrder.id,
                identifier: receiveOrder.entity_identifier,
                status: receiveOrder.status,
                orderDate: receiveOrder.orderDate,
                logFormNumber: receiveOrder.entity_data?.log_form_number,
              }
            : undefined
        }
      />
      <EditPurchaseRequisitionModal
        open={Boolean(editOrder)}
        onClose={handleCloseEdit}
        order={
          editOrder
            ? {
                id: editOrder.id,
                identifier: editOrder.entity_identifier,
                status: editOrder.status,
                orderDate: editOrder.orderDate,
                logFormNumber: editOrder.entity_data?.log_form_number,
              }
            : undefined
        }
      />
      <CancelOrderConfirmationDialog
        open={Boolean(cancelOrder)}
        orderIdentifier={cancelOrder?.entity_identifier ?? ''}
        cancellationReason={cancellationReason}
        onCancellationReasonChange={setCancellationReason}
        onCancel={handleCloseCancelDialog}
        onConfirm={handleConfirmCancel}
        isSubmitting={cancelPurchaseRequisition.isPending}
      />
      <DocumentFieldsModal
        open={docFieldsModalOpen}
        onClose={handleDocFieldsDismiss}
        onConfirm={handleDocFieldsConfirm}
        onSkip={handleDocFieldsDismiss}
        previewData={docPreviewData}
        isGenerating={isGeneratingDoc}
        title="Asset Acquisition Order"
        subtitle="Review and fill in any additional fields before generating the document."
        confirmLabel="Generate Document"
        skipLabel="Skip Document"
        cancelLabel="Close"
      />
      <PriceUpdateConfirmationModal
        open={priceUpdateModalOpen}
        priceChanges={pendingPriceChanges}
        onAccept={handlePriceModalAccept}
        onSkip={handlePriceModalSkip}
        onCancel={handlePriceModalCancel}
        isSubmitting={completePurchaseRequisition.isPending}
      />
    </Container>
  );
};
